import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { patientsApi, payersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  Plus, MagnifyingGlass, PencilSimple, Eye, Users, UserCircle,
  Phone, EnvelopeSimple, CaretDown, CaretUp, UploadSimple,
  CloudArrowDown, FunnelSimple, FileCsv, Hospital, ArrowsClockwise,
  Gear, X, CheckCircle, WarningCircle, Info,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const GENDER_COLORS = {
  male: 'bg-blue-100 text-blue-700',
  female: 'bg-pink-100 text-pink-700',
  other: 'bg-slate-100 text-slate-600',
};

const emptyForm = {
  uhid: '', name: '', dob: '', gender: '', phone: '', email: '',
  address: '', city: '', state: '', pincode: '', policy_number: '', payer_id: '', ip_no: '',
};

const PatientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [payers, setPayers] = useState([]);
  const [allPayers, setAllPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [hisDialogOpen, setHisDialogOpen] = useState(false);

  const [formData, setFormData] = useState({ ...emptyForm });
  const [editingPatient, setEditingPatient] = useState(null);

  // HIS
  const [hisSettings, setHisSettings] = useState({ endpoint_url: '', api_key: '', auth_type: 'bearer', description: '' });
  const [hisFetching, setHisFetching] = useState(false);
  const [hisSaving, setHisSaving] = useState(false);
  const [hisPushKey, setHisPushKey] = useState('');
  const [hisPushEnabled, setHisPushEnabled] = useState(false);
  const [hisTab, setHisTab] = useState('push-api');
  const [keyGenerating, setKeyGenerating] = useState(false);

  // CSV
  const [csvUploading, setCsvUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [patientsRes, payersActiveRes, payersAllRes] = await Promise.all([
        patientsApi.list(search),
        payersApi.listActive(),
        payersApi.list(),
      ]);
      setPatients(patientsRes.data);
      setPayers(payersActiveRes.data);
      setAllPayers(payersAllRes.data);
    } catch { toast.error('Failed to load patients'); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.uhid || !formData.name) { toast.error('UHID and Name are required'); return; }
    try {
      await patientsApi.create(formData);
      toast.success('Patient created successfully!');
      setAddDialogOpen(false);
      setFormData({ ...emptyForm });
      loadData();
      const redirect = searchParams.get('redirect');
      if (redirect) navigate(redirect);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create patient'); }
  };

  const handleEdit = (p) => {
    setEditingPatient(p);
    setFormData({
      uhid: p.uhid || '', name: p.name || '', dob: p.dob || '', gender: p.gender || '',
      phone: p.phone || '', email: p.email || '', address: p.address || '',
      city: p.city || '', state: p.state || '', pincode: p.pincode || '',
      policy_number: p.policy_number || '', payer_id: p.payer_id || '', ip_no: p.ip_no || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.uhid || !formData.name) { toast.error('UHID and Name are required'); return; }
    try {
      await patientsApi.update(editingPatient.id, formData);
      toast.success('Patient updated successfully!');
      setEditDialogOpen(false);
      setEditingPatient(null);
      setFormData({ ...emptyForm });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update patient'); }
  };

  // CSV Import
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await patientsApi.importCsv(fd);
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} row errors found`);
      }
      loadData();
      setImportDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'CSV import failed'); }
    finally {
      setCsvUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // HIS
  const loadHisSettings = async () => {
    try {
      const res = await patientsApi.getHisSettings();
      if (res.data) {
        setHisSettings(prev => ({ ...prev, ...res.data }));
        setHisPushKey(res.data.push_api_key || '');
        setHisPushEnabled(res.data.push_enabled || false);
      }
    } catch {}
  };

  const saveHisSettings = async () => {
    setHisSaving(true);
    try {
      await patientsApi.updateHisSettings(hisSettings);
      toast.success('HIS API configuration saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save HIS settings'); }
    finally { setHisSaving(false); }
  };

  const fetchFromHis = async () => {
    setHisFetching(true);
    try {
      const res = await patientsApi.fetchHis();
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to fetch from HIS'); }
    finally { setHisFetching(false); }
  };

  const generatePushKey = async () => {
    setKeyGenerating(true);
    try {
      const res = await patientsApi.generateHisKey();
      setHisPushKey(res.data.api_key);
      setHisPushEnabled(true);
      toast.success('API key generated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate key'); }
    finally { setKeyGenerating(false); }
  };

  const togglePushApi = async (enabled) => {
    try {
      await patientsApi.toggleHisPush({ enabled });
      setHisPushEnabled(enabled);
      toast.success(`HIS Push API ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Payer lookup map (all payers including inactive for name resolution)
  const payerMap = {};
  allPayers.forEach(p => { payerMap[p.id] = p.name; });

  // Filter + Sort
  const filtered = patients
    .filter(p => {
      if (genderFilter !== 'all' && (p.gender || '').toLowerCase() !== genderFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const av = (a[sortField] || '').toString().toLowerCase();
      const bv = (b[sortField] || '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const stats = {
    total: patients.length,
    male: patients.filter(p => (p.gender || '').toLowerCase() === 'male').length,
    female: patients.filter(p => (p.gender || '').toLowerCase() === 'female').length,
    withPolicy: patients.filter(p => p.policy_number).length,
    withEmail: patients.filter(p => p.email).length,
  };

  const SortHeader = ({ field, children }) => (
    <button className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold"
      onClick={() => { setSortField(field); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
      {children}
      {sortField === field && (sortDir === 'asc' ? <CaretUp className="h-3 w-3" /> : <CaretDown className="h-3 w-3" />)}
    </button>
  );

  const patientFormFields = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>UHID *</Label>
          <Input value={formData.uhid} onChange={e => handleChange('uhid', e.target.value)} placeholder="Unique Hospital ID" required data-testid="patient-uhid-input" /></div>
        <div><Label>Full Name *</Label>
          <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="Patient full name" required data-testid="patient-name-input" /></div>
        <div><Label>IP No</Label>
          <Input value={formData.ip_no} onChange={e => handleChange('ip_no', e.target.value)} placeholder="Inpatient Number" data-testid="patient-ip-no-input" /></div>
        <div><Label>Date of Birth</Label>
          <Input type="date" value={formData.dob} onChange={e => handleChange('dob', e.target.value)} /></div>
        <div><Label>Gender</Label>
          <Select value={formData.gender} onValueChange={v => handleChange('gender', v)}>
            <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>{GENDER_OPTIONS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Phone</Label>
          <Input value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+91 XXXXXXXXXX" data-testid="patient-phone-input" /></div>
        <div><Label>Email</Label>
          <Input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="patient@email.com" /></div>
      </div>
      <div><Label>Address</Label>
        <Input value={formData.address} onChange={e => handleChange('address', e.target.value)} placeholder="Street address" /></div>
      <div className="grid grid-cols-3 gap-4">
        <div><Label>City</Label><Input value={formData.city} onChange={e => handleChange('city', e.target.value)} /></div>
        <div><Label>State</Label><Input value={formData.state} onChange={e => handleChange('state', e.target.value)} /></div>
        <div><Label>Pincode</Label><Input value={formData.pincode} onChange={e => handleChange('pincode', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Policy Number</Label>
          <Input value={formData.policy_number} onChange={e => handleChange('policy_number', e.target.value)} placeholder="Insurance policy number" /></div>
        <div><Label>Default Payer/TPA</Label>
          <Select value={formData.payer_id} onValueChange={v => handleChange('payer_id', v)}>
            <SelectTrigger><SelectValue placeholder="Select payer" /></SelectTrigger>
            <SelectContent>{payers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" /></div>;

  return (
    <div className="space-y-6" data-testid="patients-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="patients-title">Patients</h1>
          <p className="text-sm text-slate-500 mt-1">Manage patient records and HIS integration</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {user?.role === 'hospital_admin' && (
            <>
              <Button variant="outline" className="gap-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                onClick={() => { loadHisSettings(); setHisDialogOpen(true); }} data-testid="his-integration-btn">
                <Hospital className="h-4 w-4" /> HIS Integration
              </Button>
              <Button variant="outline" className="gap-2 border-amber-200 text-amber-600 hover:bg-amber-50"
                onClick={() => setImportDialogOpen(true)} data-testid="import-csv-btn">
                <FileCsv className="h-4 w-4" /> Import CSV
              </Button>
            </>
          )}
          <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) setFormData({ ...emptyForm }); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2" data-testid="add-patient-btn">
                <Plus className="h-4 w-4" /> Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Add New Patient</DialogTitle>
                <DialogDescription>Enter patient details to register them in the system.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {patientFormFields}
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="submit" className="bg-gradient-to-r from-sky-500 to-blue-600 text-white" data-testid="save-patient-btn">Save Patient</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-800' },
          { label: 'Male', value: stats.male, color: 'text-blue-700' },
          { label: 'Female', value: stats.female, color: 'text-pink-700' },
          { label: 'With Policy', value: stats.withPolicy, color: 'text-emerald-700' },
          { label: 'With Email', value: stats.withEmail, color: 'text-violet-700' },
        ].map(s => (
          <Card key={s.label} className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-3 pb-3 text-center">
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by name, UHID, IP No, or phone..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9 border-slate-300" data-testid="patient-search-input" />
        </form>
        <Button variant="outline" size="sm" onClick={() => { setSearch(''); loadData(); }} className="border-slate-300">
          <ArrowsClockwise className="h-4 w-4" />
        </Button>
        <FunnelSimple className="h-4 w-4 text-slate-400" />
        <Select value={genderFilter} onValueChange={v => { setGenderFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32 border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Gender</SelectItem>
            {GENDER_OPTIONS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400">{filtered.length} patient{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="patients-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-500"><SortHeader field="uhid">UHID</SortHeader></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">IP No</TableHead>
                  <TableHead className="text-slate-500"><SortHeader field="name">Name</SortHeader></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Gender</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Contact</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">City</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Policy #</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer / TPA</TableHead>
                  <TableHead className="text-slate-500"><SortHeader field="created_at">Registered</SortHeader></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map(p => (
                  <TableRow key={p.id} className="hover:bg-sky-50/50 transition-colors" data-testid={`patient-row-${p.id}`}>
                    <TableCell>
                      <span className="font-medium text-sky-700 text-sm">{p.uhid}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 font-medium">{p.ip_no || <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold bg-gradient-to-br from-sky-400 to-violet-400">
                          {p.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-slate-800 text-sm">{p.name}</span>
                            {p.source === 'his_push' && (
                              <span className="text-[8px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded font-semibold">HIS</span>
                            )}
                          </div>
                          {p.dob && <span className="text-[10px] text-slate-400">DOB: {p.dob}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {p.gender ? (
                        <Badge className={`${GENDER_COLORS[p.gender.toLowerCase()] || 'bg-slate-100 text-slate-600'} border-0 text-[10px] capitalize`}>
                          {p.gender}
                        </Badge>
                      ) : <span className="text-xs text-slate-300">-</span>}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {p.phone && <div className="flex items-center gap-1 text-xs text-slate-600"><Phone className="h-3 w-3 text-slate-400" />{p.phone}</div>}
                        {p.email && <div className="flex items-center gap-1 text-[10px] text-slate-400"><EnvelopeSimple className="h-3 w-3" />{p.email}</div>}
                        {!p.phone && !p.email && <span className="text-xs text-slate-300">-</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{p.city || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{p.policy_number || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {p.payer_id && payerMap[p.payer_id]
                        ? <span className="text-orange-600 font-medium">{payerMap[p.payer_id]}</span>
                        : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        <Button variant="ghost" size="sm" className="hover:bg-sky-50 text-sky-600 h-7 w-7 p-0"
                          onClick={() => handleEdit(p)} data-testid={`edit-patient-${p.id}`} title="Edit">
                          <PencilSimple className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {paginated.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">{patients.length === 0 ? 'No patients yet. Add patients or import from HIS.' : 'No patients match the filters'}</p>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">
                Showing {((page - 1) * perPage) + 1}-{Math.min(page * perPage, filtered.length)} of {filtered.length}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">Prev</Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pn = page <= 3 ? i + 1 : page + i - 2;
                  if (pn < 1 || pn > totalPages) return null;
                  return (
                    <Button key={pn} variant={pn === page ? 'default' : 'outline'} size="sm"
                      onClick={() => setPage(pn)} className={`h-7 w-7 text-xs ${pn === page ? 'bg-sky-500 text-white' : ''}`}>{pn}</Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingPatient(null); setFormData({ ...emptyForm }); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit Patient</DialogTitle>
            <DialogDescription>Update patient details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {patientFormFields}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="submit" className="bg-gradient-to-r from-sky-500 to-blue-600 text-white" data-testid="save-patient-btn">Update Patient</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><FileCsv className="h-5 w-5 text-amber-500" /> Import Patients from CSV</DialogTitle>
            <DialogDescription>Upload a CSV file with patient data. Duplicate UHIDs will be skipped.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-medium text-amber-800 mb-2">Required CSV Columns:</p>
              <code className="text-[10px] text-amber-700 bg-amber-100 px-2 py-1 rounded block">
                uhid, name, ip_no, dob, gender, phone, email, address, city, state, pincode, policy_number
              </code>
              <p className="text-[10px] text-amber-600 mt-2">Only <strong>uhid</strong> and <strong>name</strong> are mandatory. Other columns are optional.</p>
            </div>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-sky-300 transition-colors">
              <UploadSimple className="h-8 w-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600 mb-2">Click to upload CSV file</p>
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvUpload}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-sky-50 file:text-sky-600 hover:file:bg-sky-100"
                data-testid="csv-file-input" disabled={csvUploading} />
              {csvUploading && <p className="text-xs text-sky-600 mt-2 animate-pulse">Importing...</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* HIS Integration Dialog */}
      <Dialog open={hisDialogOpen} onOpenChange={setHisDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Hospital className="h-5 w-5 text-emerald-500" /> HIS Integration</DialogTitle>
            <DialogDescription>Connect your Hospital Information System to sync patient data with Care'O CMC.</DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 mt-2">
            {[
              { id: 'push-api', label: 'Push API (Recommended)', icon: CloudArrowDown },
              { id: 'pull-config', label: 'Pull from HIS', icon: ArrowsClockwise },
            ].map(tab => (
              <button key={tab.id} onClick={() => setHisTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${hisTab === tab.id ? 'border-sky-500 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                <tab.icon className="h-3.5 w-3.5" /> {tab.label}
              </button>
            ))}
          </div>

          {/* Push API Tab */}
          {hisTab === 'push-api' && (
            <div className="space-y-4 pt-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1"><Info className="h-3.5 w-3.5" /> How Push API Works</p>
                <p className="text-[10px] text-emerald-700 mt-1">Your HIS system calls our API endpoint whenever a new patient is registered or updated. Patient data flows <strong>one-way from HIS to Care'O CMC</strong> in real-time.</p>
              </div>

              {/* API Key Section */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Your HIS API Key</p>
                    <p className="text-[10px] text-slate-500">Use this key in the <code className="bg-slate-100 px-1 rounded">X-HIS-API-Key</code> header</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {hisPushKey && (
                      <button onClick={() => togglePushApi(!hisPushEnabled)}
                        className={`text-[10px] px-2 py-1 rounded-full font-medium ${hisPushEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}
                        data-testid="toggle-push-api">
                        {hisPushEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    )}
                  </div>
                </div>
                {hisPushKey ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-900 text-emerald-400 px-3 py-2 rounded-md text-xs font-mono select-all overflow-x-auto"
                      data-testid="his-push-api-key">{hisPushKey}</code>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(hisPushKey)} className="shrink-0 h-8 text-xs">Copy</Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No API key generated yet.</p>
                )}
                <Button onClick={generatePushKey} disabled={keyGenerating} variant="outline"
                  className="gap-2 text-xs border-emerald-200 text-emerald-600 hover:bg-emerald-50" data-testid="generate-key-btn">
                  <Gear className="h-3.5 w-3.5" /> {keyGenerating ? 'Generating...' : hisPushKey ? 'Regenerate Key' : 'Generate API Key'}
                </Button>
              </div>

              {/* API Documentation */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-800">API Documentation</p>

                {/* Endpoint 1: Single Patient */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700 border-0 text-[9px] font-bold">POST</Badge>
                      <span className="text-xs font-medium text-slate-700">Push Single Patient</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => copyToClipboard(`${baseUrl}/api/his/push-patient`)}>Copy URL</Button>
                  </div>
                  <code className="block bg-slate-900 text-sky-300 px-3 py-1.5 rounded text-[10px] font-mono overflow-x-auto" data-testid="push-single-url">
                    {baseUrl}/api/his/push-patient
                  </code>
                  <details className="group">
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700 font-medium">View cURL Example</summary>
                    <pre className="mt-2 bg-slate-900 text-slate-300 p-3 rounded-md text-[10px] font-mono overflow-x-auto whitespace-pre-wrap">
{`curl -X POST "${baseUrl}/api/his/push-patient" \\
  -H "Content-Type: application/json" \\
  -H "X-HIS-API-Key: ${hisPushKey || '<YOUR_API_KEY>'}" \\
  -d '{
    "uhid": "PAT-12345",
    "name": "Rajesh Kumar",
    "ip_no": "IP-2026-001",
    "dob": "1985-03-15",
    "gender": "male",
    "phone": "9876543210",
    "email": "rajesh@email.com",
    "city": "Mumbai",
    "state": "Maharashtra",
    "pincode": "400001",
    "policy_number": "POL-9876"
  }'`}
                    </pre>
                  </details>
                </div>

                {/* Endpoint 2: Bulk Patients */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700 border-0 text-[9px] font-bold">POST</Badge>
                      <span className="text-xs font-medium text-slate-700">Push Bulk Patients (max 500)</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => copyToClipboard(`${baseUrl}/api/his/push-patients`)}>Copy URL</Button>
                  </div>
                  <code className="block bg-slate-900 text-sky-300 px-3 py-1.5 rounded text-[10px] font-mono overflow-x-auto" data-testid="push-bulk-url">
                    {baseUrl}/api/his/push-patients
                  </code>
                  <details className="group">
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700 font-medium">View cURL Example</summary>
                    <pre className="mt-2 bg-slate-900 text-slate-300 p-3 rounded-md text-[10px] font-mono overflow-x-auto whitespace-pre-wrap">
{`curl -X POST "${baseUrl}/api/his/push-patients" \\
  -H "Content-Type: application/json" \\
  -H "X-HIS-API-Key: ${hisPushKey || '<YOUR_API_KEY>'}" \\
  -d '{
    "patients": [
      {"uhid":"PAT-001","name":"Rajesh Kumar","ip_no":"IP-001","gender":"male","phone":"9876543210","city":"Mumbai"},
      {"uhid":"PAT-002","name":"Priya Sharma","ip_no":"IP-002","gender":"female","phone":"9876543211","city":"Delhi"}
    ]
  }'`}
                    </pre>
                  </details>
                </div>

                {/* Endpoint 3: Status Check */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700 border-0 text-[9px] font-bold">GET</Badge>
                      <span className="text-xs font-medium text-slate-700">Check Connection Status</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => copyToClipboard(`${baseUrl}/api/his/status`)}>Copy URL</Button>
                  </div>
                  <code className="block bg-slate-900 text-sky-300 px-3 py-1.5 rounded text-[10px] font-mono overflow-x-auto">
                    {baseUrl}/api/his/status
                  </code>
                </div>

                {/* Field Mapping */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-700 mb-2">Accepted Patient Fields</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {[
                      { field: 'uhid / patient_id / mr_number', required: true },
                      { field: 'name / patient_name', required: true },
                      { field: 'ip_no / ip_number / inpatient_no', required: false },
                      { field: 'dob / date_of_birth', required: false },
                      { field: 'gender / sex', required: false },
                      { field: 'phone / mobile / contact', required: false },
                      { field: 'email', required: false },
                      { field: 'address', required: false },
                      { field: 'city', required: false },
                      { field: 'state', required: false },
                      { field: 'pincode / zip', required: false },
                      { field: 'policy_number / policy', required: false },
                    ].map(f => (
                      <div key={f.field} className="flex items-center gap-1 text-[10px]">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${f.required ? 'bg-red-400' : 'bg-slate-300'}`} />
                        <code className="text-slate-600">{f.field}</code>
                        {f.required && <span className="text-red-500 text-[8px]">required</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-[9px] text-slate-400 mt-2">Existing patients (matched by UHID) will be <strong>updated</strong>. New UHIDs will be <strong>created</strong>.</p>
                </div>
              </div>
            </div>
          )}

          {/* Pull Config Tab */}
          {hisTab === 'pull-config' && (
            <div className="space-y-4 pt-2">
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <p className="text-xs font-medium text-sky-800 flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Pull Mode</p>
                <p className="text-[10px] text-sky-700 mt-1">Configure your HIS API endpoint and manually pull patient data on-demand.</p>
              </div>
              <div><Label>HIS API Endpoint URL *</Label>
                <Input value={hisSettings.endpoint_url} onChange={e => setHisSettings(s => ({ ...s, endpoint_url: e.target.value }))}
                  placeholder="https://your-his.hospital.com/api/patients" data-testid="his-endpoint-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Auth Type</Label>
                  <Select value={hisSettings.auth_type} onValueChange={v => setHisSettings(s => ({ ...s, auth_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      <SelectItem value="x-api-key">X-API-Key</SelectItem>
                      <SelectItem value="basic">Basic Auth</SelectItem>
                      <SelectItem value="none">No Auth</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label>API Key / Token</Label>
                  <Input type="password" value={hisSettings.api_key} onChange={e => setHisSettings(s => ({ ...s, api_key: e.target.value }))}
                    placeholder="Your HIS API key" /></div>
              </div>
              <div><Label>Description (optional)</Label>
                <Input value={hisSettings.description} onChange={e => setHisSettings(s => ({ ...s, description: e.target.value }))}
                  placeholder="e.g., Medisys HIS v3.2" /></div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveHisSettings} disabled={hisSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-1"
                  data-testid="save-his-btn">
                  <Gear className="h-4 w-4" /> {hisSaving ? 'Saving...' : 'Save Config'}
                </Button>
                <Button onClick={fetchFromHis} disabled={hisFetching || !hisSettings.endpoint_url}
                  className="bg-gradient-to-r from-sky-500 to-blue-600 text-white gap-2 flex-1" data-testid="fetch-his-btn">
                  <CloudArrowDown className="h-4 w-4" /> {hisFetching ? 'Fetching...' : 'Fetch Patients'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientsPage;
